import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Snackbar,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { DateTime } from 'luxon';
import {
  AcceptedPaymentMethod,
  CountryCode,
  CurrencyType,
  Features,
  PaymentMethodType,
  UpdateTenantAccountAcceptedPaymentTypesInput,
  UpdateTenantAccountFeatureFlagsInput,
  UpdateTenantAccountSettingsInput,
} from '../../gql-types.generated';
import {
  fetchTenantById,
  updateTenantAcceptedPaymentTypes,
  updateTenantFeatureFlags,
  updateTenantSettings,
} from './TenantSettingsActions';
import {
  clearFeatureFlagsUpdateStatus,
  clearPaymentTypesUpdateStatus,
  clearTenantSettings,
  clearUpdateStatus,
  selectTenantSettingsAccount,
  selectTenantSettingsError,
  selectTenantSettingsLoading,
  selectTenantSettingsUpdateError,
  selectTenantSettingsUpdateFeatureFlagsError,
  selectTenantSettingsUpdateFeatureFlagsLoading,
  selectTenantSettingsUpdateFeatureFlagsSuccess,
  selectTenantSettingsUpdateLoading,
  selectTenantSettingsUpdatePaymentTypesError,
  selectTenantSettingsUpdatePaymentTypesLoading,
  selectTenantSettingsUpdatePaymentTypesSuccess,
  selectTenantSettingsUpdateSuccess,
} from './TenantSettingsSlice';
import { RoutePath } from '../../util/Routes';
import {
  checkIsCountryCodeValid,
  checkIsEmailValid,
  checkIsPhoneNumberValid,
  checkIsPostalCodeValid,
} from '../../util/PaymentVirtualTerminalValidators';
import VerificationStatusIndicator from '../../components/VerificationStatusIndicator';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(3),
      flex: 1,
      overflow: 'auto',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(3),
    },
    section: {
      padding: theme.spacing(3),
      marginBottom: theme.spacing(3),
      borderRadius: 10,
      border: `1px solid ${theme.palette.divider}`,
      overflow: 'hidden',
    },
    sectionActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      marginTop: theme.spacing(3),
      gap: theme.spacing(1),
    },
    editRow: {
      display: 'flex',
      alignItems: 'flex-start',
      padding: theme.spacing(0.75, 0),
      borderBottom: `1px solid ${theme.palette.divider}`,
      gap: theme.spacing(2),
      '&:last-child': {
        borderBottom: 'none',
      },
      '& .MuiInputBase-input': {
        fontSize: 15,
      },
      '& .MuiSelect-select': {
        fontSize: 15,
      },
    },
    editLabel: {
      fontSize: 12,
      fontWeight: 500,
      letterSpacing: 1.5,
      textTransform: 'uppercase' as const,
      color: theme.palette.text.secondary,
      width: 180,
      flexShrink: 0,
      paddingTop: 4,
    },
    loadingOverlay: {
      display: 'flex',
      justifyContent: 'center',
      padding: theme.spacing(6),
    },
    // Read-only rows: align the small-caps label to the value's text baseline. Declared after
    // editRow so it overrides its `alignItems: flex-start` (sx can't beat the makeStyles rule).
    readRowBaseline: {
      alignItems: 'baseline',
    },
    // Checkbox edit rows: the label sits next to a checkbox (not a text input), so drop the
    // top padding tuned for inputs and let the row center the label against the checkbox.
    checkboxRowLabel: {
      paddingTop: 0,
    },
  }),
);

type FeatureFlagKey = keyof UpdateTenantAccountFeatureFlagsInput;

interface FeatureFlagOption {
  key: FeatureFlagKey;
  label: string;
  // Reads the current value from the (nested) account features model.
  read: (features?: Features | null) => boolean;
}

const featureFlagOptions: FeatureFlagOption[] = [
  { key: 'customers', label: 'Customers', read: f => f?.customers.enabled ?? false },
  { key: 'creditMemos', label: 'Credit Memos', read: f => f?.creditMemos.enabled ?? false },
  { key: 'customEmailSettings', label: 'Custom Email Settings', read: f => f?.customEmailSettings.enabled ?? false },
  { key: 'allowVoid', label: 'Void Unsettled Payments', read: f => f?.payments.allowVoid ?? false },
  { key: 'convenienceFees', label: 'Fee Recovery', read: f => f?.payments.convenienceFees ?? false },
  { key: 'virtualTerminal', label: 'Virtual Terminal', read: f => f?.payments.virtualTerminal ?? false },
  { key: 'consolidatedPayment', label: 'Consolidated Payments', read: f => f?.paymentRequests.consolidatedPayment ?? false },
  { key: 'partialPayment', label: 'Partial Payments', read: f => f?.paymentRequests.partialPayment ?? false },
  { key: 'paymentReminderEmails', label: 'Payment Reminder Emails', read: f => f?.paymentRequests.paymentReminderEmails ?? false },
  { key: 'autoUpdatePaymentMethods', label: 'Auto-Update Payment Methods', read: f => f?.paymentMethods.autoUpdate ?? false },
  { key: 'paymentMethodRequests', label: 'Payment Method Requests', read: f => f?.paymentMethods.paymentMethodRequests ?? false },
];

const readFeatureFlags = (features?: Features | null): Record<FeatureFlagKey, boolean> => {
  const result = {} as Record<FeatureFlagKey, boolean>;
  featureFlagOptions.forEach(opt => {
    result[opt.key] = opt.read(features);
  });
  return result;
};

const TenantSettings: FC = () => {
  const classes = useStyles();
  const { tenantId } = useParams<{ tenantId: string }>();
  const history = useHistory();
  const dispatch = useDispatch();

  const tenantAccount = useSelector(selectTenantSettingsAccount);
  const loading = useSelector(selectTenantSettingsLoading);
  const fetchError = useSelector(selectTenantSettingsError);
  const updateLoading = useSelector(selectTenantSettingsUpdateLoading);
  const updateError = useSelector(selectTenantSettingsUpdateError);
  const updateSuccess = useSelector(selectTenantSettingsUpdateSuccess);
  const updatePaymentTypesLoading = useSelector(selectTenantSettingsUpdatePaymentTypesLoading);
  const updatePaymentTypesError = useSelector(selectTenantSettingsUpdatePaymentTypesError);
  const updatePaymentTypesSuccess = useSelector(selectTenantSettingsUpdatePaymentTypesSuccess);
  const updateFeatureFlagsLoading = useSelector(selectTenantSettingsUpdateFeatureFlagsLoading);
  const updateFeatureFlagsError = useSelector(selectTenantSettingsUpdateFeatureFlagsError);
  const updateFeatureFlagsSuccess = useSelector(selectTenantSettingsUpdateFeatureFlagsSuccess);

  const [isEditing, setIsEditing] = useState(false);

  const [isEditingPaymentTypes, setIsEditingPaymentTypes] = useState(false);
  const [selectedSupportedMethods, setSelectedSupportedMethods] = useState<PaymentMethodType[]>([]);
  const [restrictPaymentMethodTypes, setRestrictPaymentMethodTypes] = useState(false);
  const [selectedCardTypes, setSelectedCardTypes] = useState<AcceptedPaymentMethod[]>([]);
  const [paymentTypesValidationError, setPaymentTypesValidationError] = useState('');

  const [isEditingFeatureFlags, setIsEditingFeatureFlags] = useState(false);
  const [selectedFeatureFlags, setSelectedFeatureFlags] = useState<Record<FeatureFlagKey, boolean>>(() => readFeatureFlags());

  const [companyName, setCompanyName] = useState('');
  const [dba, setDba] = useState('');
  const [merchantId, setMerchantId] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [addressCity, setAddressCity] = useState('');
  const [addressRegion, setAddressRegion] = useState('');
  const [addressPostalCode, setAddressPostalCode] = useState('');
  const [addressCountry, setAddressCountry] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [primaryEmail, setPrimaryEmail] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [statementDescription, setStatementDescription] = useState('');
  const [refundPolicy, setRefundPolicy] = useState('');
  const [currency, setCurrency] = useState('');
  const [country, setCountry] = useState('');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (tenantId) {
      dispatch(fetchTenantById(tenantId));
    }
    return () => {
      dispatch(clearTenantSettings());
    };
  }, [dispatch, tenantId]);

  useEffect(() => {
    if (updateSuccess) {
      setIsEditing(false);
    }
  }, [updateSuccess]);

  useEffect(() => {
    if (updatePaymentTypesSuccess) {
      setIsEditingPaymentTypes(false);
    }
  }, [updatePaymentTypesSuccess]);

  useEffect(() => {
    if (updateFeatureFlagsSuccess) {
      setIsEditingFeatureFlags(false);
    }
  }, [updateFeatureFlagsSuccess]);

  const populateForm = () => {
    if (!tenantAccount) return;
    setCompanyName(tenantAccount.company?.name ?? '');
    setDba(tenantAccount.businessProfile?.name ?? '');
    setMerchantId(tenantAccount.payfac?.ids?.[0]?.resourceId ?? '');
    setAddressLine1(tenantAccount.company?.address?.line1 ?? '');
    setAddressLine2(tenantAccount.company?.address?.line2 ?? '');
    setAddressCity(tenantAccount.company?.address?.city ?? '');
    setAddressRegion(tenantAccount.company?.address?.region ?? '');
    setAddressPostalCode(tenantAccount.company?.address?.postalCode ?? '');
    setAddressCountry(tenantAccount.company?.address?.country ?? '');
    setPhoneCountryCode(tenantAccount.company?.phone?.countryCode ?? '');
    setPhoneNumber(tenantAccount.company?.phone?.number ?? '');
    setPrimaryEmail(tenantAccount.email ?? '');
    setSupportEmail(tenantAccount.businessProfile?.supportEmail ?? '');
    setStatementDescription(tenantAccount.settings?.cardPayments?.statementDescription ?? '');
    setRefundPolicy(tenantAccount.settings?.cardPayments?.refundPolicy ?? '');
    setCurrency(tenantAccount.defaultCurrency ?? '');
    setCountry(tenantAccount.country ?? '');
    setValidationErrors({});
  };

  const handleEnterEdit = () => {
    populateForm();
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setValidationErrors({});
  };

  const isFormValid = (): boolean => {
    const errors: Record<string, string> = {};
    if (!companyName.trim()) errors.companyName = 'Company Name is required.';
    if (!dba.trim()) errors.dba = 'Doing Business As (DBA) is required.';
    if (!merchantId.trim()) errors.merchantId = 'Merchant ID is required.';
    if (primaryEmail && !checkIsEmailValid(primaryEmail)) errors.primaryEmail = 'Invalid email';
    if (supportEmail && !checkIsEmailValid(supportEmail)) errors.supportEmail = 'Invalid email';
    if (phoneCountryCode && !checkIsCountryCodeValid(phoneCountryCode)) errors.phoneCountryCode = 'Invalid country code';
    if (phoneNumber && !checkIsPhoneNumberValid(phoneNumber)) errors.phoneNumber = 'Invalid phone number';
    if (addressPostalCode && !checkIsPostalCodeValid(addressPostalCode, addressCountry)) {
      errors.addressPostalCode = 'Invalid postal code';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpdate = () => {
    if (!isFormValid()) return;
    const orNull = (value: string): string | null => value || null;
    const input: UpdateTenantAccountSettingsInput = {
      companyName: companyName || undefined,
      businessProfileName: dba || undefined,
      merchantId: merchantId || undefined,
      address: {
        line1: orNull(addressLine1),
        line2: orNull(addressLine2),
        city: orNull(addressCity),
        region: orNull(addressRegion),
        postalCode: orNull(addressPostalCode),
        country: orNull(addressCountry),
      },
      phone: {
        countryCode: orNull(phoneCountryCode),
        number: orNull(phoneNumber),
      },
      primaryAccountHolderEmail: orNull(primaryEmail),
      supportEmail: orNull(supportEmail),
      statementDescription: orNull(statementDescription),
      refundPolicy: orNull(refundPolicy),
      currency: (currency as CurrencyType) || undefined,
      country: (country as CountryCode) || undefined,
    };
    dispatch(updateTenantSettings(tenantId, input));
  };

  const supportedPaymentMethodOptions: { value: PaymentMethodType; label: string }[] = [
    { value: PaymentMethodType.CreditCard, label: 'Credit Card' },
    { value: PaymentMethodType.PaymentBankUs, label: 'Bank (ACH)' },
  ];

  const cardBrandOptions: { value: AcceptedPaymentMethod; label: string }[] = [
    { value: AcceptedPaymentMethod.Visa, label: 'Visa' },
    { value: AcceptedPaymentMethod.Mastercard, label: 'Mastercard' },
    { value: AcceptedPaymentMethod.Amex, label: 'American Express' },
    { value: AcceptedPaymentMethod.Discover, label: 'Discover' },
    { value: AcceptedPaymentMethod.Jcb, label: 'JCB' },
    { value: AcceptedPaymentMethod.Diners, label: 'Diners Club' },
  ];
  const bankPaymentMethodOption = { value: AcceptedPaymentMethod.PaymentBank, label: 'Bank' };

  const labelForSupportedMethod = (method: PaymentMethodType): string =>
    supportedPaymentMethodOptions.find(o => o.value === method)?.label ?? method;
  const labelForPaymentMethodType = (method: AcceptedPaymentMethod): string =>
    [...cardBrandOptions, bankPaymentMethodOption].find(o => o.value === method)?.label ?? method;

  // Only the card brands we expose as options — excludes PAYMENT_BANK / UNKNOWN.
  const acceptedCardBrands = (tenantAccount?.acceptedPaymentMethods ?? []).filter(m => cardBrandOptions.some(o => o.value === m));

  // Card types are only accepted when Credit Card is an enabled supported method; Bank (ACH) is accepted
  // whenever it is an enabled supported method, regardless of any card-type restriction.
  const creditCardSupported = (tenantAccount?.settings?.supportedPaymentMethods ?? []).includes(PaymentMethodType.CreditCard);
  const achSupported = (tenantAccount?.settings?.supportedPaymentMethods ?? []).includes(PaymentMethodType.PaymentBankUs);
  // The card-type portion of the display: only present when Credit Card is supported. When supported but
  // unrestricted (no specific brands selected) it reads "All card types"; otherwise the selected brands.
  const restrictedCardBrandLabels = creditCardSupported ? acceptedCardBrands.map(labelForPaymentMethodType) : [];
  const cardTypesDisplayParts =
    creditCardSupported && acceptedCardBrands.length === 0 ? ['All card types'] : restrictedCardBrandLabels;
  const acceptedPaymentTypesDisplay =
    [...cardTypesDisplayParts, ...(achSupported ? [bankPaymentMethodOption.label] : [])].join(', ') || null;

  const creditCardSelected = selectedSupportedMethods.includes(PaymentMethodType.CreditCard);
  const achEnabled = selectedSupportedMethods.includes(PaymentMethodType.PaymentBankUs);

  // The accepted payment method types offered under the card-type restriction. This is more than card
  // brands: Bank (ACH) is included and locked on whenever ACH is enabled, since the restriction governs
  // every accepted payment method type — leaving Bank out would drop ACH.
  const acceptedPaymentMethodTypeOptions: { value: AcceptedPaymentMethod; label: string; locked: boolean }[] = [
    ...cardBrandOptions.map(o => ({ ...o, locked: false })),
    ...(achEnabled ? [{ ...bankPaymentMethodOption, locked: true }] : []),
  ];

  const handleEnterPaymentTypesEdit = () => {
    setSelectedSupportedMethods(tenantAccount?.settings?.supportedPaymentMethods ?? []);
    setRestrictPaymentMethodTypes(acceptedCardBrands.length > 0);
    setSelectedCardTypes(acceptedCardBrands);
    setPaymentTypesValidationError('');
    setIsEditingPaymentTypes(true);
  };

  const handleCancelPaymentTypes = () => {
    setIsEditingPaymentTypes(false);
    setPaymentTypesValidationError('');
  };

  const handleSupportedMethodChange = (method: PaymentMethodType, checked: boolean) => {
    setSelectedSupportedMethods(prev => (checked ? [...prev, method] : prev.filter(m => m !== method)));
  };

  const handleCardTypeChange = (cardType: AcceptedPaymentMethod, checked: boolean) => {
    setSelectedCardTypes(prev => (checked ? [...prev, cardType] : prev.filter(c => c !== cardType)));
  };

  const handleUpdatePaymentTypes = () => {
    if (selectedSupportedMethods.length === 0) {
      setPaymentTypesValidationError('At least one supported payment method is required.');
      return;
    }
    // Card-type restriction only applies when Credit Card is an enabled method.
    const restrictCardTypes = restrictPaymentMethodTypes && selectedSupportedMethods.includes(PaymentMethodType.CreditCard);
    if (restrictCardTypes && selectedCardTypes.length === 0) {
      setPaymentTypesValidationError('Select at least one card type, or turn off the card type restriction.');
      return;
    }
    setPaymentTypesValidationError('');
    // The restriction applies to all payment method types, so Bank (ACH) must stay in the
    // accepted list whenever ACH is enabled — otherwise restricting card types would drop ACH.
    const acceptedPaymentMethodTypes = restrictCardTypes
      ? [...selectedCardTypes, ...(achEnabled ? [AcceptedPaymentMethod.PaymentBank] : [])]
      : undefined;
    const input: UpdateTenantAccountAcceptedPaymentTypesInput = {
      supportedPaymentMethods: selectedSupportedMethods,
      restrictPaymentMethodTypes: restrictCardTypes,
      acceptedPaymentMethodTypes,
    };
    dispatch(updateTenantAcceptedPaymentTypes(tenantId, input));
  };

  const handleEnterFeatureFlagsEdit = () => {
    setSelectedFeatureFlags(readFeatureFlags(tenantAccount?.settings?.features));
    setIsEditingFeatureFlags(true);
  };

  const handleCancelFeatureFlags = () => {
    setIsEditingFeatureFlags(false);
  };

  const handleFeatureFlagChange = (key: FeatureFlagKey, checked: boolean) => {
    setSelectedFeatureFlags(prev => ({ ...prev, [key]: checked }));
  };

  const handleUpdateFeatureFlags = () => {
    const input: UpdateTenantAccountFeatureFlagsInput = { ...selectedFeatureFlags };
    dispatch(updateTenantFeatureFlags(tenantId, input));
  };

  const formatDate = (isoDate?: string | null): string => {
    if (!isoDate) return '—';
    const dt = DateTime.fromISO(isoDate);
    return dt.isValid ? dt.toFormat('MMM d, yyyy') : '—';
  };

  const val = (v?: string | null) => (
    <Typography variant="body1" sx={{ flex: 1, minWidth: 0, pt: 0.5, wordBreak: 'break-word' }}>
      {v || '—'}
    </Typography>
  );

  // Enabled/Disabled status with an at-a-glance icon, matching the Account Preferences screen.
  const featureStatus = (isEnabled: boolean) => (
    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, pt: 0.5 }}>
      {isEnabled ? (
        <DoneIcon fontSize="small" color="success" sx={{ mr: 0.5 }} />
      ) : (
        <CloseIcon fontSize="small" color="error" sx={{ mr: 0.5 }} />
      )}
      <Typography variant="body1">{isEnabled ? 'Enabled' : 'Disabled'}</Typography>
    </Box>
  );

  if (loading && !tenantAccount) {
    return (
      <Paper className={classes.root}>
        <Box className={classes.loadingOverlay}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (fetchError) {
    return (
      <Paper className={classes.root}>
        <Box className={classes.header}>
          <IconButton size="small" onClick={() => history.push(RoutePath.Home)} aria-label="Back to all tenants">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6">Tenant Settings</Typography>
        </Box>
        <Alert severity="error">{fetchError.message}</Alert>
      </Paper>
    );
  }

  const ta = tenantAccount;

  return (
    <Paper className={classes.root}>
      <Helmet>
        <title>Aptean Pay - Tenant Settings</title>
      </Helmet>

      <Box className={classes.header}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Tooltip title="Back to All Tenants">
            <IconButton
              size="small"
              onClick={() => history.push(RoutePath.Home)}
              aria-label="Back to all tenants"
              data-cy="back-to-tenants"
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h6">{ta?.company?.name ?? 'Tenant Settings'}</Typography>
        </Box>
      </Box>

      {/* Section 1: Tenant Settings */}
      <Paper elevation={0} className={classes.section}>
        <Box
          sx={{
            backgroundColor: 'grey.100',
            px: 3,
            py: 1,
            mx: -3,
            mt: -3,
            mb: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="subtitle">Tenant Settings</Typography>
          {!isEditing && (
            <Button size="small" variant="outlined" onClick={handleEnterEdit} data-cy="btn-edit-tenant-settings">
              Edit
            </Button>
          )}
        </Box>

        {!isEditing ? (
          /* ── Readonly view ── */
          <Grid container columnSpacing={3}>
            <Grid item xs={12}>
              <Box className={classes.editRow}>
                <Typography className={classes.editLabel}>Tenant ID</Typography>
                <Typography variant="body1" sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
                  {ta?.owner?.tenantId || '—'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Box className={classes.editRow}>
                <Typography className={classes.editLabel}>Verification Status</Typography>
                <Box sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
                  <VerificationStatusIndicator status={ta?.company?.verification?.status} />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Box className={classes.editRow}>
                <Typography className={classes.editLabel}>Created Date</Typography>
                <Typography variant="body1" sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
                  {formatDate(ta?.createdAt)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Box className={classes.editRow}>
                <Typography className={classes.editLabel}>Onboarded Date</Typography>
                <Typography variant="body1" sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
                  {formatDate(ta?.tosAcceptance?.date)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Box className={classes.editRow}>
                <Typography className={classes.editLabel}>Company Name</Typography>
                {val(ta?.company?.name)}
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Box className={classes.editRow}>
                <Typography className={classes.editLabel}>Doing Business As (DBA)</Typography>
                {val(ta?.businessProfile?.name)}
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Box className={classes.editRow}>
                <Typography className={classes.editLabel}>Merchant ID</Typography>
                {val(ta?.payfac?.ids?.[0]?.resourceId)}
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Box className={classes.editRow}>
                <Typography className={classes.editLabel}>Currency</Typography>
                {val(ta?.defaultCurrency)}
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Box className={classes.editRow}>
                <Typography className={classes.editLabel}>Country</Typography>
                {val(ta?.country)}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ backgroundColor: 'grey.100', px: 3, py: 1, mx: -3, my: 1 }}>
                <Typography variant="subtitle">Address</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Box className={classes.editRow}>
                <Typography className={classes.editLabel}>Line 1</Typography>
                {val(ta?.company?.address?.line1)}
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Box className={classes.editRow}>
                <Typography className={classes.editLabel}>Line 2</Typography>
                {val(ta?.company?.address?.line2)}
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Box className={classes.editRow}>
                <Typography className={classes.editLabel}>City</Typography>
                {val(ta?.company?.address?.city)}
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Box className={classes.editRow}>
                <Typography className={classes.editLabel}>Region / State</Typography>
                {val(ta?.company?.address?.region)}
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Box className={classes.editRow}>
                <Typography className={classes.editLabel}>Postal Code</Typography>
                {val(ta?.company?.address?.postalCode)}
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Box className={classes.editRow}>
                <Typography className={classes.editLabel}>Address Country</Typography>
                {val(ta?.company?.address?.country)}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ backgroundColor: 'grey.100', px: 3, py: 1, mx: -3, my: 1 }}>
                <Typography variant="subtitle">Phone</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Box className={classes.editRow}>
                <Typography className={classes.editLabel}>Country Code</Typography>
                {val(ta?.company?.phone?.countryCode)}
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Box className={classes.editRow}>
                <Typography className={classes.editLabel}>Phone Number</Typography>
                {val(ta?.company?.phone?.number)}
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Box className={classes.editRow}>
                <Typography className={classes.editLabel}>Primary Account Holder Email</Typography>
                {val(ta?.email)}
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Box className={classes.editRow}>
                <Typography className={classes.editLabel}>Support Email</Typography>
                {val(ta?.businessProfile?.supportEmail)}
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Box className={classes.editRow}>
                <Typography className={classes.editLabel}>Statement Description</Typography>
                {val(ta?.settings?.cardPayments?.statementDescription)}
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Box className={classes.editRow}>
                <Typography className={classes.editLabel}>Refund Policy</Typography>
                {val(ta?.settings?.cardPayments?.refundPolicy)}
              </Box>
            </Grid>
          </Grid>
        ) : (
          /* ── Edit mode ── */
          <>
            <Grid container columnSpacing={3} sx={{ mb: 2 }}>
              <Grid item xs={12}>
                <Box className={classes.editRow}>
                  <Typography className={classes.editLabel}>Tenant ID</Typography>
                  <Typography variant="body1" sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
                    {ta?.owner?.tenantId || '—'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Box className={classes.editRow}>
                  <Typography className={classes.editLabel}>Verification Status</Typography>
                  <Box sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
                    <VerificationStatusIndicator status={ta?.company?.verification?.status} />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Box className={classes.editRow}>
                  <Typography className={classes.editLabel}>Created Date</Typography>
                  <Typography variant="body1" sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
                    {formatDate(ta?.createdAt)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Box className={classes.editRow}>
                  <Typography className={classes.editLabel}>Onboarded Date</Typography>
                  <Typography variant="body1" sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
                    {formatDate(ta?.tosAcceptance?.date)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            <Grid container columnSpacing={2}>
              <Grid item xs={12} lg={6}>
                <Box className={classes.editRow}>
                  <Typography className={classes.editLabel}>Company Name *</Typography>
                  <TextField
                    value={companyName}
                    onChange={e => setCompanyName(e.target.value)}
                    size="small"
                    sx={{ flex: 1, minWidth: 0 }}
                    error={!!validationErrors.companyName}
                    helperText={validationErrors.companyName ?? ''}
                    inputProps={{ 'data-cy': 'field-company-name' }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Box className={classes.editRow}>
                  <Typography className={classes.editLabel}>Doing Business As (DBA) *</Typography>
                  <TextField
                    value={dba}
                    onChange={e => setDba(e.target.value)}
                    size="small"
                    sx={{ flex: 1, minWidth: 0 }}
                    error={!!validationErrors.dba}
                    helperText={validationErrors.dba ?? ''}
                    inputProps={{ 'data-cy': 'field-dba' }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Box className={classes.editRow}>
                  <Typography className={classes.editLabel}>Merchant ID *</Typography>
                  <TextField
                    value={merchantId}
                    onChange={e => setMerchantId(e.target.value)}
                    size="small"
                    sx={{ flex: 1, minWidth: 0 }}
                    error={!!validationErrors.merchantId}
                    helperText={validationErrors.merchantId ?? ''}
                    inputProps={{ 'data-cy': 'field-merchant-id' }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Box className={classes.editRow}>
                  <Typography className={classes.editLabel}>Currency</Typography>
                  <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                    <Select
                      value={currency}
                      onChange={(e: SelectChangeEvent) => setCurrency(e.target.value)}
                      inputProps={{ 'data-cy': 'field-currency' }}
                    >
                      <MenuItem value={CurrencyType.Usd}>USD</MenuItem>
                      <MenuItem value={CurrencyType.Cad}>CAD</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Box className={classes.editRow}>
                  <Typography className={classes.editLabel}>Country</Typography>
                  <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                    <Select
                      value={country}
                      onChange={(e: SelectChangeEvent) => setCountry(e.target.value)}
                      inputProps={{ 'data-cy': 'field-country' }}
                    >
                      <MenuItem value={CountryCode.Us}>US</MenuItem>
                      <MenuItem value={CountryCode.Ca}>CA</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ backgroundColor: 'grey.100', px: 3, py: 1, mx: -3, my: 1 }}>
                  <Typography variant="subtitle">Address</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Box className={classes.editRow}>
                  <Typography className={classes.editLabel}>Line 1</Typography>
                  <TextField
                    value={addressLine1}
                    onChange={e => setAddressLine1(e.target.value)}
                    size="small"
                    sx={{ flex: 1, minWidth: 0 }}
                    inputProps={{ 'data-cy': 'field-address-line1' }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Box className={classes.editRow}>
                  <Typography className={classes.editLabel}>Line 2</Typography>
                  <TextField
                    value={addressLine2}
                    onChange={e => setAddressLine2(e.target.value)}
                    size="small"
                    sx={{ flex: 1, minWidth: 0 }}
                    inputProps={{ 'data-cy': 'field-address-line2' }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Box className={classes.editRow}>
                  <Typography className={classes.editLabel}>City</Typography>
                  <TextField
                    value={addressCity}
                    onChange={e => setAddressCity(e.target.value)}
                    size="small"
                    sx={{ flex: 1, minWidth: 0 }}
                    inputProps={{ 'data-cy': 'field-address-city' }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Box className={classes.editRow}>
                  <Typography className={classes.editLabel}>Region / State</Typography>
                  <TextField
                    value={addressRegion}
                    onChange={e => setAddressRegion(e.target.value)}
                    size="small"
                    sx={{ flex: 1, minWidth: 0 }}
                    inputProps={{ 'data-cy': 'field-address-region' }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Box className={classes.editRow}>
                  <Typography className={classes.editLabel}>Postal Code</Typography>
                  <TextField
                    value={addressPostalCode}
                    onChange={e => setAddressPostalCode(e.target.value)}
                    size="small"
                    sx={{ flex: 1, minWidth: 0 }}
                    error={!!validationErrors.addressPostalCode}
                    helperText={validationErrors.addressPostalCode ?? ''}
                    inputProps={{ 'data-cy': 'field-address-postal-code' }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Box className={classes.editRow}>
                  <Typography className={classes.editLabel}>Address Country</Typography>
                  <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                    <Select
                      value={addressCountry}
                      onChange={(e: SelectChangeEvent) => setAddressCountry(e.target.value)}
                      inputProps={{ 'data-cy': 'field-address-country' }}
                    >
                      <MenuItem value="US">US</MenuItem>
                      <MenuItem value="CA">CA</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ backgroundColor: 'grey.100', px: 3, py: 1, mx: -3, my: 1 }}>
                  <Typography variant="subtitle">Phone</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Box className={classes.editRow}>
                  <Typography className={classes.editLabel}>Country Code</Typography>
                  <TextField
                    value={phoneCountryCode}
                    onChange={e => setPhoneCountryCode(e.target.value)}
                    size="small"
                    sx={{ flex: 1, minWidth: 0 }}
                    placeholder="+1"
                    error={!!validationErrors.phoneCountryCode}
                    helperText={validationErrors.phoneCountryCode ?? ''}
                    inputProps={{ 'data-cy': 'field-phone-country-code' }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Box className={classes.editRow}>
                  <Typography className={classes.editLabel}>Phone Number</Typography>
                  <TextField
                    value={phoneNumber}
                    onChange={e => setPhoneNumber(e.target.value)}
                    size="small"
                    sx={{ flex: 1, minWidth: 0 }}
                    error={!!validationErrors.phoneNumber}
                    helperText={validationErrors.phoneNumber ?? ''}
                    inputProps={{ 'data-cy': 'field-phone-number' }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Box className={classes.editRow}>
                  <Typography className={classes.editLabel}>Primary Account Holder Email</Typography>
                  <TextField
                    value={primaryEmail}
                    onChange={e => setPrimaryEmail(e.target.value)}
                    size="small"
                    type="email"
                    sx={{ flex: 1, minWidth: 0 }}
                    error={!!validationErrors.primaryEmail}
                    helperText={validationErrors.primaryEmail ?? ''}
                    inputProps={{ 'data-cy': 'field-primary-email' }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Box className={classes.editRow}>
                  <Typography className={classes.editLabel}>Support Email</Typography>
                  <TextField
                    value={supportEmail}
                    onChange={e => setSupportEmail(e.target.value)}
                    size="small"
                    type="email"
                    sx={{ flex: 1, minWidth: 0 }}
                    error={!!validationErrors.supportEmail}
                    helperText={validationErrors.supportEmail ?? ''}
                    inputProps={{ 'data-cy': 'field-support-email' }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Box className={classes.editRow}>
                  <Typography className={classes.editLabel}>Statement Description</Typography>
                  <TextField
                    value={statementDescription}
                    onChange={e => setStatementDescription(e.target.value)}
                    size="small"
                    sx={{ flex: 1, minWidth: 0 }}
                    inputProps={{ 'data-cy': 'field-statement-description' }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Box className={classes.editRow}>
                  <Typography className={classes.editLabel}>Refund Policy</Typography>
                  <TextField
                    value={refundPolicy}
                    onChange={e => setRefundPolicy(e.target.value)}
                    size="small"
                    multiline
                    minRows={2}
                    sx={{ flex: 1, minWidth: 0 }}
                    inputProps={{ 'data-cy': 'field-refund-policy' }}
                  />
                </Box>
              </Grid>
            </Grid>

            <Box className={classes.sectionActions}>
              {updateLoading && <CircularProgress size={20} />}
              <Button variant="outlined" onClick={handleCancel} disabled={updateLoading} data-cy="btn-cancel-tenant-settings">
                Cancel
              </Button>
              <Button variant="contained" onClick={handleUpdate} disabled={updateLoading} data-cy="btn-update-tenant-settings">
                Update
              </Button>
            </Box>
          </>
        )}
      </Paper>

      {/* Section 2: Accepted Payment Method Types */}
      <Paper elevation={0} className={classes.section}>
        <Box
          sx={{
            backgroundColor: 'grey.100',
            px: 3,
            py: 1,
            mx: -3,
            mt: -3,
            mb: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="subtitle">Accepted Payment Method Types</Typography>
          {!isEditingPaymentTypes && (
            <Button size="small" variant="outlined" onClick={handleEnterPaymentTypesEdit} data-cy="btn-edit-payment-types">
              Edit
            </Button>
          )}
        </Box>

        {!isEditingPaymentTypes ? (
          /* ── Readonly view ── */
          <Grid container columnSpacing={3}>
            <Grid item xs={12}>
              <Box className={`${classes.editRow} ${classes.readRowBaseline}`}>
                <Typography className={classes.editLabel}>Supported Methods</Typography>
                {val((ta?.settings?.supportedPaymentMethods ?? []).map(labelForSupportedMethod).join(', ') || null)}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box className={`${classes.editRow} ${classes.readRowBaseline}`}>
                <Typography className={classes.editLabel}>Accepted Types</Typography>
                {val(acceptedPaymentTypesDisplay)}
              </Box>
            </Grid>
          </Grid>
        ) : (
          /* ── Edit view ── */
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 0.75, gap: 2 }}>
              <Typography className={`${classes.editLabel} ${classes.checkboxRowLabel}`}>Supported Methods</Typography>
              <FormControl component="fieldset" sx={{ flex: 1, minWidth: 0 }}>
                <FormGroup row sx={{ columnGap: 4 }}>
                  {supportedPaymentMethodOptions.map(opt => (
                    <FormControlLabel
                      key={opt.value}
                      data-cy={`chk-supported-${opt.value}`}
                      control={
                        <Checkbox
                          checked={selectedSupportedMethods.includes(opt.value)}
                          onChange={e => handleSupportedMethodChange(opt.value, e.target.checked)}
                          size="small"
                        />
                      }
                      label={opt.label}
                      sx={{ mr: 0, '& .MuiFormControlLabel-label': { whiteSpace: 'nowrap' } }}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Box>

            {creditCardSelected && (
              <Box sx={{ py: 0.75 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography className={`${classes.editLabel} ${classes.checkboxRowLabel}`}>Payment Method Types</Typography>
                  <FormControlLabel
                    data-cy="chk-restrict-card-types"
                    control={
                      <Checkbox
                        checked={restrictPaymentMethodTypes}
                        onChange={e => setRestrictPaymentMethodTypes(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Restrict accepted payment method types"
                    sx={{ mr: 0 }}
                  />
                </Box>
                {restrictPaymentMethodTypes && (
                  <FormControl component="fieldset" sx={{ display: 'block', mt: 0.5, pl: 'calc(180px + 16px)' }}>
                    <FormGroup row sx={{ columnGap: 4 }}>
                      {acceptedPaymentMethodTypeOptions.map(opt => {
                        const checkbox = opt.locked ? (
                          <Checkbox checked disabled size="small" />
                        ) : (
                          <Checkbox
                            checked={selectedCardTypes.includes(opt.value)}
                            onChange={e => handleCardTypeChange(opt.value, e.target.checked)}
                            size="small"
                          />
                        );
                        const control = (
                          <FormControlLabel
                            key={opt.value}
                            data-cy={`chk-card-${opt.value}`}
                            control={checkbox}
                            label={opt.label}
                            sx={{ mr: 0, minWidth: 150, '& .MuiFormControlLabel-label': { whiteSpace: 'nowrap' } }}
                          />
                        );
                        return opt.locked ? (
                          <Tooltip key={opt.value} title="Bank (ACH) is enabled for this tenant and is always accepted.">
                            {control}
                          </Tooltip>
                        ) : (
                          control
                        );
                      })}
                    </FormGroup>
                  </FormControl>
                )}
              </Box>
            )}

            {paymentTypesValidationError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {paymentTypesValidationError}
              </Alert>
            )}

            <Box className={classes.sectionActions} sx={{ mb: 1.5 }}>
              {updatePaymentTypesLoading && <CircularProgress size={20} />}
              <Button
                variant="outlined"
                onClick={handleCancelPaymentTypes}
                disabled={updatePaymentTypesLoading}
                data-cy="btn-cancel-payment-types"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleUpdatePaymentTypes}
                disabled={updatePaymentTypesLoading}
                data-cy="btn-update-payment-types"
              >
                Update
              </Button>
            </Box>
          </>
        )}
      </Paper>

      {/* Section 3: Feature Flags */}
      <Paper elevation={0} className={classes.section}>
        <Box
          sx={{
            backgroundColor: 'grey.100',
            px: 3,
            py: 1,
            mx: -3,
            mt: -3,
            mb: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="subtitle">Feature Flags</Typography>
          {!isEditingFeatureFlags && (
            <Button size="small" variant="outlined" onClick={handleEnterFeatureFlagsEdit} data-cy="btn-edit-feature-flags">
              Edit
            </Button>
          )}
        </Box>

        {!isEditingFeatureFlags ? (
          /* ── Readonly view ── */
          <Grid container columnSpacing={3}>
            {featureFlagOptions.map(opt => (
              <Grid item xs={12} lg={6} key={opt.key}>
                <Box className={`${classes.editRow} ${classes.readRowBaseline}`}>
                  <Typography className={classes.editLabel}>{opt.label}</Typography>
                  {featureStatus(opt.read(ta?.settings?.features))}
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : (
          /* ── Edit view ── */
          <>
            <Grid container columnSpacing={3}>
              {featureFlagOptions.map(opt => (
                <Grid item xs={12} sm={6} key={opt.key}>
                  <Box sx={{ py: 0.5 }}>
                    <FormControlLabel
                      data-cy={`chk-feature-${opt.key}`}
                      control={
                        <Checkbox
                          checked={selectedFeatureFlags[opt.key]}
                          onChange={e => handleFeatureFlagChange(opt.key, e.target.checked)}
                          size="small"
                        />
                      }
                      label={opt.label}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Box className={classes.sectionActions} sx={{ mb: 1.5 }}>
              {updateFeatureFlagsLoading && <CircularProgress size={20} />}
              <Button
                variant="outlined"
                onClick={handleCancelFeatureFlags}
                disabled={updateFeatureFlagsLoading}
                data-cy="btn-cancel-feature-flags"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleUpdateFeatureFlags}
                disabled={updateFeatureFlagsLoading}
                data-cy="btn-update-feature-flags"
              >
                Update
              </Button>
            </Box>
          </>
        )}
      </Paper>

      <Snackbar
        open={updateSuccess}
        autoHideDuration={4000}
        onClose={() => dispatch(clearUpdateStatus())}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => dispatch(clearUpdateStatus())}>
          Tenant settings updated successfully.
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!updateError}
        autoHideDuration={6000}
        onClose={() => dispatch(clearUpdateStatus())}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => dispatch(clearUpdateStatus())}>
          {updateError?.message ?? 'An error occurred while updating tenant settings.'}
        </Alert>
      </Snackbar>

      <Snackbar
        open={updatePaymentTypesSuccess}
        autoHideDuration={4000}
        onClose={() => dispatch(clearPaymentTypesUpdateStatus())}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => dispatch(clearPaymentTypesUpdateStatus())}>
          Accepted payment methods updated successfully.
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!updatePaymentTypesError}
        autoHideDuration={6000}
        onClose={() => dispatch(clearPaymentTypesUpdateStatus())}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => dispatch(clearPaymentTypesUpdateStatus())}>
          {updatePaymentTypesError?.message ?? 'An error occurred while updating accepted payment methods.'}
        </Alert>
      </Snackbar>

      <Snackbar
        open={updateFeatureFlagsSuccess}
        autoHideDuration={4000}
        onClose={() => dispatch(clearFeatureFlagsUpdateStatus())}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => dispatch(clearFeatureFlagsUpdateStatus())}>
          Feature flags updated successfully.
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!updateFeatureFlagsError}
        autoHideDuration={6000}
        onClose={() => dispatch(clearFeatureFlagsUpdateStatus())}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => dispatch(clearFeatureFlagsUpdateStatus())}>
          {updateFeatureFlagsError?.message ?? 'An error occurred while updating feature flags.'}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default TenantSettings;
